import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Formik, Form, FieldArray } from 'formik';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import { $sduiTicketData } from '@features/tickets/models/detail-sdui';
import { $worksPending } from '@features/tickets/models/ticket-works';
import { DetailToolbar, CustomScrollbar } from '@ui/index';
import { AddButton } from '../../atoms';
import {
  $works,
  $modeWorks,
  changeWorksMode,
  setTicketWorks,
  $ticket,
  pushEmptytWorks,
} from '../../models';
import { WorkCard } from '../../molecules';
import { useStyles } from '../detail/components/styles';
import { useStyles as useStylesWorks } from './styles';
import { validationSchema } from './validation';

export const TicketWorks: FC = () => {
  const { t } = useTranslation();
  const [works, modeWorks, ticket, sduiTicketData, worksPending] = useUnit([
    $works,
    $modeWorks,
    $ticket,
    $sduiTicketData,
    $worksPending,
  ]);
  const classes = useStyles({ external: false });
  const classesWorks = useStylesWorks();

  const status = typeof ticket?.status === 'object' ? ticket.status.id : ticket.status;
  const statusSdui = sduiTicketData?.status;
  const isHideEditButton = status !== 'new' && statusSdui !== 'new';

  return (
    <>
      <Backdrop open={worksPending}>
        <CircularProgress />
      </Backdrop>

      <Formik
        initialValues={{ works }}
        validationSchema={validationSchema}
        onSubmit={setTicketWorks}
        enableReinitialize
        render={({ values, resetForm, errors }) => {
          const hasError = Object.values(errors).length > 0 || values.works.length === 0;

          const onEdit = () => changeWorksMode('edit');

          const onCancel = () => {
            resetForm();
            changeWorksMode('view');
          };

          const onClick = () => {
            changeWorksMode('edit');
            pushEmptytWorks();
          };

          return (
            <Form className={classesWorks.form}>
              <DetailToolbar
                className={classes.toolbar}
                mode={modeWorks}
                hidden={{ edit: isHideEditButton, close: true, delete: true }}
                onEdit={onEdit}
                onCancel={onCancel}
                hasError={hasError}
              />
              <div className={classes.formContent}>
                <CustomScrollbar>
                  <div className={classes.content}>
                    <FieldArray
                      name="works"
                      render={({ push, remove }) => {
                        const worksElements = values.works.map((work, index) => (
                          <WorkCard
                            work={work}
                            index={index}
                            key={work._id}
                            push={push}
                            remove={remove}
                          />
                        ));

                        const isDisabledAddButton =
                          values.works.length === 1 ? !values.works[0]?.device : false;

                        return (
                          <>
                            {values.works.length === 0 && (
                              <Typography
                                variant="caption"
                                gutterBottom
                                sx={{ display: 'block', textAlign: 'center' }}
                              >
                                {t('NoWorks')}
                              </Typography>
                            )}
                            {worksElements}
                            {(modeWorks === 'edit' || values.works.length === 0) &&
                              !isHideEditButton && (
                                <AddButton
                                  disabled={isDisabledAddButton}
                                  onClick={onClick}
                                />
                              )}
                          </>
                        );
                      }}
                    />
                  </div>
                </CustomScrollbar>
              </div>
            </Form>
          );
        }}
      />
    </>
  );
};
